import { useState, useEffect } from 'react'
import { useNavigation } from '@react-navigation/core'
import styled from 'styled-components'
import { StyleSheet, Text, TouchableOpacity, View, SafeAreaView, ImageBackground, Platform } from 'react-native'
import { useRecoilValue } from 'recoil'
import { userState } from '../state/atoms'
import { Heading, Button, Box, Progress, Divider } from 'native-base'
import LottieView from 'lottie-react-native'
import { FontAwesome5 } from '@expo/vector-icons'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { db, auth } from '../firebase'
import { addDoc, collection, increment, serverTimestamp, updateDoc, doc, query, where, getDocs, onSnapshot } from 'firebase/firestore'

// import { Model } from '../components/Model'

const Stats = styled(View)`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: space-between;
    justify-content: center;
    margin-top: 10px;
`

const StatsBox = styled(Box)`
    width: 90px;
    height: 100px;
    margin: 0 10px;
    align-items: center;
    justify-content: center;
`

const BoldText = styled(Heading)`
    font-size: 20px;
    font-weight: bold;
    margin: 0;
`

const StatNumber = styled(BoldText)`
    font-size: 40px;
    padding-top: 10px;
`

const MinorText = styled(Text)`
    font-size: 14px;
    color: gray;
    margin: 0;
    text-align: 'center';
`

const HomeScreen = () => {
    // Helper variables
    const navigation = useNavigation()
    const userID = auth?.currentUser?.uid
    const user = useRecoilValue(userState)
    const [pendingHomes, setPendingHomes] = useState(0)
    const [pendingPoints, setPendingPoints] = useState(0)

    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, 'homes'), (snapshot) => {
            // get number of homes that have tag_owner_ID === userID and review_status === 'pending'
            const numOfHomes = snapshot.docs.filter((doc) => doc.data().tag_owner_ID === userID && doc.data().review_status === 'pending').length
            setPendingHomes(numOfHomes)
            // get number of homes that have tag_owner_ID === userID and sum all of the potential points
            const potentialPoints = snapshot.docs.filter((doc) => doc.data().tag_owner_ID === userID).reduce((acc, doc) => acc + doc.data().potential_points, 0)
            setPendingPoints(potentialPoints)
        })
        return () => {
            unsubscribe()
        }
    }, [])

    return (
        <ImageBackground style={{ width: '100%', height: '100%', alignSelf: 'center' }}>
            {Platform.OS !== 'web' && <LottieView source={require('../assets/animations/particles.json')} style={{ opacity: 0.8 }} autoPlay loop />}
            <SafeAreaView style={styles.container}>
                <View style={{ alignItems: 'center' }}>
                    <Text style={styles.greeting}>Welcome back{user?.first_name ? `, ${user?.first_name}!` : '!'}</Text>
                </View>
                <Box style={styles.notificationBox}>
                    <Heading>Notifications</Heading>
                    <Text>You have no new notifications. Happy hunting!</Text>
                </Box>
                <Box style={styles.rewardBox}>
                    <Heading>Earn Rewards</Heading>
                    <Text>You have no rewards available. When you earn enough points, you can redeem points for rewards.</Text>
                    <View style={styles.progressCard}>
                        <Text style={styles.progressInditcators}>0</Text>
                        <View style={styles.progressBarSection}>
                            <Progress style={{ width: '95%', opacity: 0.7, marginTop: 16 }} size={'sm'} value={user?.stats?.points} color="emerald" />
                            <View style={styles.chonkyBackground}>
                                <Text style={styles.chonkyText}>{user?.stats?.points}</Text>
                                <Text style={styles.subChonkyText}> Points Earned</Text>
                            </View>
                        </View>
                        <Text style={styles.progressInditcators}>100</Text>
                    </View>
                </Box>
                <Box style={styles.statsBox}>
                    <Heading>On Deck</Heading>
                    <Stats>
                        <StatsBox>
                            <MaterialCommunityIcons name="timer-sand" size={24} color="#6c387a" style={{ marginBottom: 10 }} />
                            <StatNumber>{pendingHomes}</StatNumber>
                            <MinorText>Reviews Pending</MinorText>
                        </StatsBox>
                        <Divider orientation="vertical" />
                        <StatsBox>
                            <FontAwesome5 name="question-circle" size={24} color="#6c387a" style={{ marginBottom: 10 }} />
                            <StatNumber>{pendingPoints}</StatNumber>
                            <MinorText>Points Pending</MinorText>
                        </StatsBox>
                        <Divider orientation="vertical" />
                        <StatsBox>
                            <MaterialCommunityIcons name="gift" size={24} color="#6c387a" style={{ marginBottom: 10 }} />
                            <StatNumber>0</StatNumber>
                            <MinorText>Rewards Claimed</MinorText>
                        </StatsBox>
                    </Stats>
                </Box>

                <Button onPress={() => navigation.navigate('Submit')} size="lg" style={styles.startButton}>
                    Claim a new home
                </Button>

                {/* Bypassing until 3d model can be enabled */}
                {/* <Model /> */}
            </SafeAreaView>
        </ImageBackground>
    )
}

export default HomeScreen

const styles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    notificationBox: {
        width: '80%',
        height: 'auto',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: 5,
        paddingBottom: 20,
        paddingTop: 10,
        paddingLeft: 20,
        paddingRight: 20,
        margin: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
    },
    rewardBox: {
        width: '80%',
        height: 'auto',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: 5,
        paddingBottom: 20,
        paddingTop: 10,
        paddingLeft: 20,
        paddingRight: 20,
        margin: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
    },
    statsBox: {
        width: '80%',
        height: 180,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: 5,
        paddingBottom: 20,
        paddingTop: 10,
        paddingLeft: 20,
        paddingRight: 20,
        margin: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
    },
    progressCard: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'stretch',
        alignItems: 'center',
        marginTop: 'auto',
        marginBottom: 'auto',
        width: '97%',
        paddingTop: 30,
    },
    progressBarSection: {
        width: '80%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    progressInditcators: {
        color: '#6c387a',
        fontSize: 32,
    },
    chonkyBackground: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
    },
    chonkyText: {
        fontWeight: 'bold',
        color: '#6c387a',
        margin: 0,
    },
    subChonkyText: {
        fontWeight: 'bold',
        color: '#6c387a',
    },
    button: {
        backgroundColor: '#6c387a',
        width: '60%',
        padding: 15,
        borderRadius: 10,
        alignItems: 'center',
        marginTop: 20,
    },
    buttonText: {
        color: 'white',
        fontWeight: '700',
        fontSize: 16,
    },
    logoText: {
        fontWeight: '700',
        fontSize: 16,
    },
    homeLogo: {
        width: '85%',
        height: undefined,
        aspectRatio: 205 / 152,
        marginTop: 50,
    },
    greeting: {
        fontSize: 28,
        fontFamily: 'Montserrat',
        fontWeight: '400',
    },
    startButton: {
        backgroundColor: '#6c387a',
        borderRadius: 5,
        alignItems: 'center',
        marginTop: 20,
    },
})
